import type { Payment } from "../lib/supabase";

export type PaymentStatus =
  | "not_received"
  | "pending"
  | "partial"
  | "completed"
  | "failed"
  | "cancelled"
  | "refunded";

/**
 * Infer payment status based on payment data and business logic
 * Since status is not stored in the database, we infer it from other fields
 */
export function inferPaymentStatus(
  payment: Payment,
  expectedAmount?: number
): PaymentStatus {
  // If payment has notes indicating failure or refund
  const notes = payment.notes?.toLowerCase() || "";

  // Check for refunded payments
  if (notes.includes("refund")) {
    return "refunded";
  }

  // Check for cancelled payments (separate from refunded)
  if (
    notes.includes("cancelled") ||
    notes.includes("canceled") ||
    notes.includes("cancel")
  ) {
    return "cancelled";
  }

  // Check for failed payments
  if (
    notes.includes("failed") ||
    notes.includes("declined") ||
    notes.includes("error") ||
    notes.includes("reject")
  ) {
    return "failed";
  }

  // Check for partial payments in notes first
  if (
    notes.includes("partial") ||
    notes.includes("installment") ||
    notes.includes("part of") ||
    notes.includes("first payment") ||
    notes.includes("second payment")
  ) {
    return "partial";
  }

  // Check for pending payments
  if (
    notes.includes("pending") ||
    notes.includes("processing") ||
    notes.includes("awaiting")
  ) {
    return "pending";
  }

  // If payment amount is 0, it means no payment has been received yet
  if (payment.amount === 0) {
    return "not_received";
  }

  // If we have an expected amount, compare to determine partial vs completed
  if (expectedAmount && expectedAmount > 0) {
    if (payment.amount > 0 && payment.amount < expectedAmount) {
      return "partial";
    }
    if (payment.amount >= expectedAmount) {
      return "completed";
    }
  }

  // If payment has an amount and date but no expected amount, consider it completed
  if (payment.amount > 0 && payment.payment_date) {
    return "completed";
  }

  // Default to not_received for new payments
  return "not_received";
}

/**
 * Get status badge color class based on payment status
 */
export function getStatusBadgeColor(status: PaymentStatus): string {
  switch (status) {
    case "completed":
      return "bg-green-100 text-green-800";
    case "partial":
      return "bg-blue-100 text-blue-800";
    case "not_received":
      return "bg-red-100 text-red-800";
    case "pending":
      return "bg-yellow-100 text-yellow-800";
    case "failed":
      return "bg-red-100 text-red-800";
    case "cancelled":
      return "bg-orange-100 text-orange-800";
    case "refunded":
      return "bg-gray-100 text-gray-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
}

export function getStatusDisplayName(status: PaymentStatus): string {
  switch (status) {
    case "not_received":
      return "Not Received";
    case "pending":
      return "Pending";
    case "partial":
      return "Partial Payment";
    case "completed":
      return "Paid";
    case "failed":
      return "Failed";
    case "cancelled":
      return "Cancelled";
    case "refunded":
      return "Refunded";
    default:
      return status;
  }
}

/**
 * Format payment amount as currency
 */
export function formatPaymentAmount(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}

/**
 * Format payment method for display
 */
export function formatPaymentMethod(
  method: string,
  paymentStatus?: PaymentStatus
): string {
  // If payment hasn't been received or method is empty, don't show a payment method
  if (paymentStatus === "not_received" || !method) {
    return "None";
  }

  const methodMap: Record<string, string> = {
    stripe: "Stripe",
    credit_card: "Credit Card",
    debit_card: "Debit Card",
    bank_transfer: "Bank Transfer",
    cash: "Cash",
    check: "Check",
    paypal: "PayPal",
    venmo: "Venmo",
    other: "Other",
  };

  return methodMap[method] || method.charAt(0).toUpperCase() + method.slice(1);
}

/**
 * Get available payment methods
 */
export function getPaymentMethods() {
  return [
    { value: "stripe", label: "Stripe" },
    { value: "credit_card", label: "Credit Card" },
    { value: "debit_card", label: "Debit Card" },
    { value: "bank_transfer", label: "Bank Transfer" },
    { value: "cash", label: "Cash" },
    { value: "check", label: "Check" },
    { value: "paypal", label: "PayPal" },
    { value: "venmo", label: "Venmo" },
    { value: "other", label: "Other" },
  ];
}
